import { VendorTransactionsLink } from '@/components/VendorTransactionsLink'
import { Head, Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import { PageHeader, PageContent } from '@/components/PageHeader'
import { FinancialYearSelect } from '@/components/FinancialYearSelect'
import { CurrencySelect } from '@/components/CurrencySelect'
import { MoneyDisplay } from '@/components/MoneyDisplay'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Money, Category, MonthlyData } from '@/types'
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  AreaChart, Area, ReferenceLine,
} from 'recharts'
import {
  ExternalLinkIcon,
  ArrowUpRightIcon,
  ArrowDownRightIcon,
  ScaleIcon,
  TrendingUpIcon,
  PiggyBankIcon,
  ActivityIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface CostByCategory {
  category: Category
  amount: Money
  prev_amount: Money
}

interface CategoryMonthlyCost {
  month: string
  label: string
  amount: Money
}

interface Props {
  display_currency: string
  revenues_sum: Money
  costs_sum: Money
  profits_sum: Money
  prev_revenues_sum: Money
  prev_costs_sum: Money
  prev_profits_sum: Money
  prev_year_name: string
  costs_month: string | null
  filtered_costs_sum: Money
  prev_filtered_costs_sum: Money
  costs_by_vendor: { vendor: string | null; category_id: number; amount: Money }[]
  costs_by_category: CostByCategory[]
  category_monthly_costs: Record<number, CategoryMonthlyCost[]>
  reimbursable_costs_sum: Money
  reimbursed_sum: Money
  reimbursement_balance: Money
  reimbursements_account_id: number
  reimbursement_transfer_category_id: number
  monthly_data: MonthlyData[]
  net_income: Money
  savings_rate: number | null
  current_balance: Money
}

const CHART_COLORS = [
  'oklch(0.42 0.12 250)',
  'oklch(0.58 0.16 165)',
  'oklch(0.50 0.14 320)',
  'oklch(0.68 0.14 70)',
  'oklch(0.55 0.18 30)',
  'oklch(0.55 0.14 200)',
  'oklch(0.50 0.18 0)',
  'oklch(0.60 0.12 280)',
  'oklch(0.65 0.16 130)',
  'oklch(0.54 0.14 45)',
]

const REVENUE_COLOR = 'oklch(0.58 0.16 155)'
const REVENUE_PREV_COLOR = 'oklch(0.58 0.08 155)'
const COSTS_COLOR = 'oklch(0.55 0.18 30)'
const COSTS_PREV_COLOR = 'oklch(0.55 0.08 30)'
const BALANCE_COLOR = 'oklch(0.42 0.12 250)'

function yoyChange(current: number, previous: number): { percent: string; trend: 'up' | 'down' } | null {
  if (previous === 0) return null
  const change = ((current - previous) / Math.abs(previous)) * 100
  return {
    percent: `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`,
    trend: change >= 0 ? 'up' : 'down',
  }
}

function moneyValue(money: Money): number {
  return money.cents / (money.currency === 'JPY' ? 1 : 100)
}

function formatChartValue(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value)
}

export default function Index({
  display_currency,
  revenues_sum,
  costs_sum,
  profits_sum,
  prev_revenues_sum,
  prev_costs_sum,
  prev_profits_sum,
  prev_year_name,
  costs_month,
  filtered_costs_sum,
  prev_filtered_costs_sum,
  costs_by_vendor,
  costs_by_category,
  category_monthly_costs,
  reimbursable_costs_sum,
  reimbursed_sum,
  reimbursement_balance,
  reimbursements_account_id,
  reimbursement_transfer_category_id,
  monthly_data,
  net_income,
  savings_rate,
  current_balance,
}: Props) {
  const [costGrouping, setCostGrouping] = useState<'category' | 'vendor'>('category')
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)
  useEffect(() => {
    if (selectedCategoryId !== null && !costs_by_category.some(item => item.category.id === selectedCategoryId)) {
      setSelectedCategoryId(null)
    }
  }, [costs_by_category, selectedCategoryId])
  const revenueCents = costs_month
    ? (monthly_data.find(m => m.month === costs_month)?.revenue.cents ?? 0)
    : revenues_sum.cents

  const pieData = costs_by_category.map((item) => ({
    id: item.category.id,
    name: item.category.name,
    value: Math.abs(item.amount.cents),
    formatted: item.amount.formatted,
  }))
  const selectedCategory = costs_by_category.find((item) => item.category.id === selectedCategoryId)
  const selectedCategoryMonthlyCosts = selectedCategoryId ? category_monthly_costs[selectedCategoryId] ?? [] : []

  const vendorTotals = new Map<string | null, number>()
  costs_by_vendor.filter(item => selectedCategoryId === null || item.category_id === selectedCategoryId).forEach(item => {
    const name = item.vendor
    vendorTotals.set(name, (vendorTotals.get(name) || 0) - moneyValue(item.amount))
  })
  const vendorData = [...vendorTotals].map(([vendor, value]) => ({ vendor, name: vendor ?? 'Not assigned', value })).filter(item => item.value > 0).sort((a, b) => b.value - a.value)

  const allVendorTotals = new Map<string | null, number>()
  costs_by_vendor.forEach(item => {
    const name = item.vendor
    allVendorTotals.set(name, (allVendorTotals.get(name) || 0) + item.amount.cents)
  })
  const vendorRows = [...allVendorTotals].sort((a, b) => a[1] - b[1])

  const revenueYoy = yoyChange(revenues_sum.cents, prev_revenues_sum.cents)
  const costsYoy = yoyChange(Math.abs(costs_sum.cents), Math.abs(prev_costs_sum.cents))
  const profitYoy = yoyChange(profits_sum.cents, prev_profits_sum.cents)

  const costsPercent = revenues_sum.cents !== 0
    ? Math.min(Math.abs(costs_sum.cents) / revenues_sum.cents * 100, 100)
    : 0
  const profitPercent = revenues_sum.cents !== 0
    ? Math.max(profits_sum.cents / revenues_sum.cents * 100, 0)
    : 0

  return (
    <>
      <Head title="Cash Flow" />

      <PageHeader title="Cash Flow" description="Your cash flow overview">
        <div className="flex items-center gap-2">
          <CurrencySelect value={display_currency} />
          <FinancialYearSelect />
        </div>
      </PageHeader>

      <PageContent className="space-y-6">

        {/* Revenue breakdown + Balance */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="lg:col-span-3">
            <CardContent className="p-5">
              {/* Revenue / Costs / Profit metrics row */}
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-6">
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] font-medium text-muted-foreground">Revenue</p>
                    {revenueYoy && (
                      <span className={cn(
                        'text-[11px] font-medium',
                        revenueYoy.trend === 'up' ? 'text-success' : 'text-destructive'
                      )}>
                        {revenueYoy.percent}
                      </span>
                    )}
                  </div>
                  <MoneyDisplay money={revenues_sum} size="xl" />
                  <p className="text-[11px] text-muted-foreground">100% of revenue</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] font-medium text-muted-foreground">Costs</p>
                    {costsYoy && (
                      <span className={cn(
                        'text-[11px] font-medium',
                        costsYoy.trend === 'up' ? 'text-destructive' : 'text-success'
                      )}>
                        {costsYoy.percent}
                      </span>
                    )}
                  </div>
                  <MoneyDisplay money={costs_sum} size="xl" />
                  <p className="text-[11px] text-muted-foreground">{costsPercent.toFixed(1)}% of revenue</p>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-1.5">
                    <p className="text-[13px] font-medium text-muted-foreground">Profit</p>
                    {profitYoy && (
                      <span className={cn(
                        'text-[11px] font-medium',
                        profitYoy.trend === 'up' ? 'text-success' : 'text-destructive'
                      )}>
                        {profitYoy.percent}
                      </span>
                    )}
                  </div>
                  <MoneyDisplay money={profits_sum} size="xl" />
                  <p className="text-[11px] text-muted-foreground">{profitPercent.toFixed(1)}% margin</p>
                </div>
              </div>

              {/* Proportional bar */}
              <div className="mt-5">
                <div className="flex h-3 w-full overflow-hidden rounded-full bg-muted/50">
                  <div
                    className="h-full rounded-l-full bg-destructive/60 transition-all"
                    style={{ width: `${costsPercent}%` }}
                  />
                  <div
                    className="h-full rounded-r-full bg-success/60 transition-all"
                    style={{ width: `${profitPercent}%` }}
                  />
                </div>
                <div className="mt-1.5 flex items-center gap-4">
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-destructive/60" />
                    <span className="text-[11px] text-muted-foreground">Costs {costsPercent.toFixed(1)}%</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="h-2 w-2 rounded-full bg-success/60" />
                    <span className="text-[11px] text-muted-foreground">Profit {profitPercent.toFixed(1)}%</span>
                  </div>
                  {revenueYoy && (
                    <span className="ml-auto text-[11px] text-muted-foreground">
                      vs {prev_year_name}
                    </span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <StatCard
            title="Balance"
            money={current_balance}
            icon={ActivityIcon}
            trend={current_balance.negative ? 'down' : 'up'}
          />
        </div>

        {/* Monthly cash flow bar chart */}
        <MonthlyCashFlowChart data={monthly_data} prevYearName={prev_year_name} />

        {/* Running balance + burn rate / runway */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <RunningBalanceChart data={monthly_data} prevYearName={prev_year_name} />
          </div>
          <div className="space-y-4">
            <Card className="overflow-hidden">
              <CardContent className="p-5 relative">
                <div className="absolute inset-0 top-auto h-[60%] opacity-[0.12]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthly_data.map((m) => ({ net: moneyValue(m.net) }))} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="netIncomeGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={net_income.negative ? 'oklch(0.55 0.18 30)' : 'oklch(0.58 0.16 155)'} stopOpacity={1} />
                          <stop offset="100%" stopColor={net_income.negative ? 'oklch(0.55 0.18 30)' : 'oklch(0.58 0.16 155)'} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="net"
                        stroke={net_income.negative ? 'oklch(0.55 0.18 30)' : 'oklch(0.58 0.16 155)'}
                        strokeWidth={1.5}
                        fill="url(#netIncomeGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-[13px] font-medium text-muted-foreground">Net Income</p>
                      <MoneyDisplay money={net_income} size="xl" />
                    </div>
                    <div className={cn(
                      'rounded-lg p-2',
                      net_income.negative ? 'bg-destructive/8 text-destructive' : 'bg-success/8 text-success'
                    )}>
                      <TrendingUpIcon className="h-4 w-4" />
                    </div>
                  </div>
                  <p className="mt-3 text-[13px] text-muted-foreground">Average monthly profit</p>
                </div>
              </CardContent>
            </Card>
            <Card className="overflow-hidden">
              <CardContent className="p-5 relative">
                <div className="absolute inset-0 top-auto h-[60%] opacity-[0.12]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={monthly_data.map((m) => ({ rate: m.revenue.cents > 0 ? (m.net.cents / m.revenue.cents) * 100 : 0 }))} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                      <defs>
                        <linearGradient id="savingsRateGradient" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={savings_rate !== null && savings_rate > 0 ? 'oklch(0.58 0.16 155)' : 'oklch(0.55 0.18 30)'} stopOpacity={1} />
                          <stop offset="100%" stopColor={savings_rate !== null && savings_rate > 0 ? 'oklch(0.58 0.16 155)' : 'oklch(0.55 0.18 30)'} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area
                        type="monotone"
                        dataKey="rate"
                        stroke={savings_rate !== null && savings_rate > 0 ? 'oklch(0.58 0.16 155)' : 'oklch(0.55 0.18 30)'}
                        strokeWidth={1.5}
                        fill="url(#savingsRateGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="relative">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <p className="text-[13px] font-medium text-muted-foreground">Savings Rate</p>
                      <p className="text-2xl font-semibold tracking-tight">
                        {savings_rate !== null ? `${savings_rate}%` : '—'}
                      </p>
                    </div>
                    <div className={cn(
                      'rounded-lg p-2',
                      savings_rate !== null && savings_rate > 0 ? 'bg-success/8 text-success' : 'bg-destructive/8 text-destructive'
                    )}>
                      <PiggyBankIcon className="h-4 w-4" />
                    </div>
                  </div>
                  <p className="mt-3 text-[13px] text-muted-foreground">Profit as % of revenue</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Costs by category */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-[15px] font-semibold">Costs by {costGrouping === 'category' ? 'Category' : 'Vendor'}</CardTitle>
                <Select
                  value={costs_month ?? '__all__'}
                  onValueChange={(value) => {
                    const params = new URLSearchParams(window.location.search)
                    if (value !== '__all__') {
                      params.set('costs_month', value)
                    } else {
                      params.delete('costs_month')
                    }

                    router.get(
                      window.location.pathname,
                      Object.fromEntries(params),
                      { preserveState: true, preserveScroll: true }
                    )
                  }}
                >
                  <SelectTrigger className="w-[140px] h-8 text-[13px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="__all__">Full Year</SelectItem>
                      {monthly_data.map((m) => (
                        <SelectItem key={m.month} value={m.month}>{m.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-1 pt-2" role="group" aria-label="Group costs by">
                {(['category', 'vendor'] as const).map(group => (
                  <button key={group} type="button" aria-pressed={costGrouping === group}
                    className={cn('rounded-md px-3 py-1.5 text-sm', costGrouping === group ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}
                    onClick={() => { setCostGrouping(group); if (group === 'vendor') setSelectedCategoryId(null) }}>
                    {group === 'category' ? 'Category' : 'Vendor'}
                  </button>
                ))}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="text-[13px] font-medium text-muted-foreground">{costGrouping === 'category' ? 'Category' : 'Vendor'}</TableHead>
                    <TableHead className="text-right text-[13px] font-medium text-muted-foreground">Amount</TableHead>
                    <TableHead className="text-right text-[13px] font-medium text-muted-foreground">% Rev</TableHead>
                    {costGrouping === 'category' && <TableHead className="text-right text-[13px] font-medium text-muted-foreground">YoY</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {costGrouping === 'category' && costs_by_category.map((item, index) => {
                    const catYoy = yoyChange(Math.abs(item.amount.cents), Math.abs(item.prev_amount.cents))
                    const pctRev = revenueCents !== 0 ? (Math.abs(item.amount.cents) / revenueCents * 100) : null
                    return (
                      <TableRow
                        key={item.category.id}
                        className={cn(
                          'group cursor-pointer',
                          selectedCategoryId === item.category.id && 'bg-muted/60'
                        )}
                        role="button"
                        tabIndex={0}
                        onClick={() => setSelectedCategoryId(item.category.id)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault()
                            setSelectedCategoryId(item.category.id)
                          }
                        }}
                      >
                        <TableCell className="py-2.5">
                          <div className="flex items-center gap-2.5">
                            <div
                              className="h-2.5 w-2.5 rounded-full shrink-0"
                              style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                            />
                            <span className="text-[13px] font-medium text-foreground">
                              {item.category.name}
                            </span>
                            <Link
                              href={`/transactions?category_id=${item.category.id}`}
                              className="opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <ExternalLinkIcon className="h-3 w-3 text-primary" />
                            </Link>
                          </div>
                        </TableCell>
                        <TableCell className="text-right py-2.5">
                          <MoneyDisplay money={item.amount} size="sm" />
                        </TableCell>
                        <TableCell className="text-right py-2.5">
                          <span className="text-[12px] text-muted-foreground">
                            {pctRev !== null ? `${pctRev.toFixed(1)}%` : '—'}
                          </span>
                        </TableCell>
                        <TableCell className="text-right py-2.5">
                          {catYoy ? (
                            <span className={cn(
                              'text-[12px] font-medium',
                              catYoy.trend === 'up' ? 'text-destructive' : 'text-success'
                            )}>
                              {catYoy.percent}
                            </span>
                          ) : (
                            <span className="text-[12px] text-muted-foreground">new</span>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                  {costGrouping === 'vendor' && vendorRows.map(([name, cents]) => (
                    <TableRow key={name ?? "__unassigned"}>
                      <TableCell className="py-2.5 text-[13px] font-medium"><span className="flex items-center gap-2">{name ?? 'Not assigned'}<VendorTransactionsLink vendor={name} /></span></TableCell>
                      <TableCell className="text-right py-2.5 text-[13px]">
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: display_currency }).format(cents / (display_currency === 'JPY' ? 1 : 100))}
                      </TableCell>
                      <TableCell className="text-right py-2.5 text-[12px] text-muted-foreground">
                        {revenueCents !== 0 ? `${(-cents / revenueCents * 100).toFixed(1)}%` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                  {costGrouping === 'vendor' && vendorRows.length === 0 && (
                    <TableRow><TableCell colSpan={3} className="py-6 text-center text-muted-foreground">No costs for this period.</TableCell></TableRow>
                  )}
                  {(() => {
                    const filteredYoy = yoyChange(Math.abs(filtered_costs_sum.cents), Math.abs(prev_filtered_costs_sum.cents))
                    return (
                      <TableRow className="border-t-2 border-border">
                        <TableCell className="py-2.5 text-[13px] font-semibold">Total</TableCell>
                        <TableCell className="text-right py-2.5">
                          <MoneyDisplay money={filtered_costs_sum} size="sm" className="font-semibold" />
                        </TableCell>
                        <TableCell className="text-right py-2.5">
                          <span className="text-[12px] font-semibold text-muted-foreground">
                            {revenueCents !== 0 ? `${(Math.abs(filtered_costs_sum.cents) / revenueCents * 100).toFixed(1)}%` : '—'}
                          </span>
                        </TableCell>
                        {costGrouping === 'category' && <TableCell className="text-right py-2.5">
                          {filteredYoy && (
                            <span className={cn(
                              'text-[12px] font-medium',
                              filteredYoy.trend === 'up' ? 'text-destructive' : 'text-success'
                            )}>
                              {filteredYoy.percent}
                            </span>
                          )}
                        </TableCell>}
                      </TableRow>
                    )
                  })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <div className="flex flex-col gap-4">
            {selectedCategory && (
              <CategoryMonthlyCostChart
                category={selectedCategory.category}
                data={selectedCategoryMonthlyCosts}
              />
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-[15px] font-semibold">Vendor Distribution</CardTitle>
                <div className="text-sm text-muted-foreground">{selectedCategory?.category.name || 'All categories'}
                  {selectedCategoryId !== null && <button className="ml-3 text-primary underline" onClick={() => setSelectedCategoryId(null)}>Clear category</button>}
                </div>
              </CardHeader>
              <CardContent>
                {vendorData.length ? <>
                  {vendorData.length > 10 && <p className="mb-2 text-xs text-muted-foreground">Top 10 of {vendorData.length} vendors; all amounts listed below.</p>}
                  <ResponsiveContainer width="100%" height={Math.max(200, Math.min(vendorData.length, 10) * 32)}>
                    <BarChart data={vendorData.slice(0, 10)} layout="vertical" margin={{ left: 12, right: 16 }}>
                      <XAxis type="number" tickFormatter={value => formatChartValue(Number(value), display_currency)} />
                      <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 12 }} tickFormatter={name => String(name).length > 22 ? `${String(name).slice(0, 21)}…` : String(name)} />
                      <Tooltip formatter={value => formatChartValue(Number(value), display_currency)} />
                      <Bar dataKey="value" name="Spend" maxBarSize={28} fill={CHART_COLORS[0]} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="max-h-64 overflow-auto">{vendorData.map(item => <div key={item.name} className="flex justify-between py-1 text-sm"><span className="flex items-center gap-2">{item.name}<VendorTransactionsLink vendor={item.vendor} categoryId={selectedCategoryId} /></span><span>{formatChartValue(item.value, display_currency)}</span></div>)}</div>
                </> : <p className="text-sm text-muted-foreground">No vendor spending in this selection.</p>}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-[15px] font-semibold">Cost Distribution</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={pieData}
                      dataKey="value"
                      nameKey="name"
                      cx="50%"
                      cy="50%"
                      innerRadius={55}
                      outerRadius={95}
                      paddingAngle={2}
                    >
                      {pieData.map((_, index) => (
                        <Cell
                          key={index}
                          fill={CHART_COLORS[index % CHART_COLORS.length]}
                          stroke="none"
                        />
                      ))}
                    </Pie>
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null
                        const data = payload[0].payload
                        return (
                          <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
                            <p className="text-[13px] font-medium text-foreground">{data.name}</p>
                            <p className="text-[13px] text-muted-foreground">{data.formatted}</p>
                          </div>
                        )
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="mt-3 flex flex-wrap justify-center gap-x-4 gap-y-2">
                  {pieData.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={cn(
                        'flex max-w-full items-center gap-1.5 text-left text-[11px] text-muted-foreground transition-colors hover:text-foreground',
                        selectedCategoryId === item.id && 'text-foreground'
                      )}
                      onClick={() => setSelectedCategoryId(item.id)}
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                      />
                      <span className="truncate">{item.name}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <MonthlySummaryCard
              monthData={costs_month ? monthly_data.find((m) => m.month === costs_month) : null}
              prevYearName={prev_year_name}
            />
          </div>
        </div>

        {/* Reimbursements */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-[15px] font-semibold flex items-center gap-2">
              <ScaleIcon className="h-4 w-4 text-muted-foreground" />
              Reimbursements
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ReimbursementCard
                title="Total Costs"
                money={reimbursable_costs_sum}
                linkHref={`/transactions?account_id=${reimbursements_account_id}`}
              />
              <ReimbursementCard
                title="Reimbursed"
                money={reimbursed_sum}
                linkHref={`/transactions?category_id=${reimbursement_transfer_category_id}`}
              />
              <ReimbursementCard
                title="Outstanding"
                money={reimbursement_balance}
                highlight
              />
            </div>
          </CardContent>
        </Card>
      </PageContent>
    </>
  )
}

/* ------------------------------------------------------------------ */
/* Sub-components                                                      */
/* ------------------------------------------------------------------ */

function MonthlyCashFlowChart({ data, prevYearName }: { data: MonthlyData[]; prevYearName: string }) {
  const hasPrevData = data.some((m) => m.prev_revenue.cents !== 0 || m.prev_costs.cents !== 0)
  const currency = data[0]?.revenue.currency ?? 'JPY'

  const chartData = data.map((m) => ({
    label: m.label,
    revenue: moneyValue(m.revenue),
    costs: Math.abs(moneyValue(m.costs)),
    prev_revenue: moneyValue(m.prev_revenue),
    prev_costs: Math.abs(moneyValue(m.prev_costs)),
  }))

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[15px] font-semibold">Monthly Cash Flow</CardTitle>
          {hasPrevData && (
            <span className="text-[11px] text-muted-foreground">
              Faded bars = {prevYearName}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData} barGap={0} barCategoryGap="20%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.91 0.004 80)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => formatChartValue(v, currency)}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                const monthData = data.find((m) => m.label === label)
                if (!monthData) return null
                return (
                  <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
                    <p className="text-[13px] font-medium text-foreground">{label}</p>
                    <p className="text-[13px]" style={{ color: REVENUE_COLOR }}>
                      Revenue: {monthData.revenue.formatted}
                    </p>
                    <p className="text-[13px]" style={{ color: COSTS_COLOR }}>
                      Costs: {monthData.costs.formatted}
                    </p>
                    <p className="text-[13px] font-medium text-foreground mt-1 pt-1 border-t border-border">
                      Net: {monthData.net.formatted}
                    </p>
                    {hasPrevData && (
                      <div className="mt-1 pt-1 border-t border-border">
                        <p className="text-[11px] text-muted-foreground">{prevYearName}</p>
                        <p className="text-[12px] text-muted-foreground">
                          Rev: {monthData.prev_revenue.formatted} / Costs: {monthData.prev_costs.formatted}
                        </p>
                      </div>
                    )}
                  </div>
                )
              }}
            />
            {hasPrevData && (
              <>
                <Bar dataKey="prev_revenue" name={`Revenue (${prevYearName})`} fill={REVENUE_PREV_COLOR} radius={[3, 3, 0, 0]} opacity={0.35} />
                <Bar dataKey="prev_costs" name={`Costs (${prevYearName})`} fill={COSTS_PREV_COLOR} radius={[3, 3, 0, 0]} opacity={0.35} />
              </>
            )}
            <Bar dataKey="revenue" name="Revenue" fill={REVENUE_COLOR} radius={[3, 3, 0, 0]} />
            <Bar dataKey="costs" name="Costs" fill={COSTS_COLOR} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function RunningBalanceChart({ data, prevYearName }: { data: MonthlyData[]; prevYearName: string }) {
  const hasPrevData = data.some((m) => m.prev_running_balance.cents !== 0)
  const currency = data[0]?.running_balance.currency ?? 'JPY'

  const chartData = data.map((m) => ({
    label: m.label,
    balance: moneyValue(m.running_balance),
    prev_balance: moneyValue(m.prev_running_balance),
  }))

  return (
    <Card className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-[15px] font-semibold">Running Balance</CardTitle>
          {hasPrevData && (
            <span className="text-[11px] text-muted-foreground">
              Dashed line = {prevYearName}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={240}>
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={BALANCE_COLOR} stopOpacity={0.15} />
                <stop offset="95%" stopColor={BALANCE_COLOR} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.91 0.004 80)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => formatChartValue(v, currency)}
            />
            <ReferenceLine y={0} stroke="oklch(0.85 0.004 80)" strokeDasharray="3 3" />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                const monthData = data.find((m) => m.label === label)
                if (!monthData) return null
                return (
                  <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
                    <p className="text-[13px] font-medium text-foreground">{label}</p>
                    <p className="text-[13px] text-muted-foreground">
                      Balance: {monthData.running_balance.formatted}
                    </p>
                    {hasPrevData && (
                      <p className="text-[12px] text-muted-foreground">
                        {prevYearName}: {monthData.prev_running_balance.formatted}
                      </p>
                    )}
                  </div>
                )
              }}
            />
            {hasPrevData && (
              <Area
                type="monotone"
                dataKey="prev_balance"
                stroke={BALANCE_COLOR}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                strokeOpacity={0.4}
                fill="none"
              />
            )}
            <Area
              type="monotone"
              dataKey="balance"
              stroke={BALANCE_COLOR}
              strokeWidth={2}
              fill="url(#balanceGradient)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function CategoryMonthlyCostChart({
  category,
  data,
}: {
  category: Category
  data: CategoryMonthlyCost[]
}) {
  const currency = data[0]?.amount.currency ?? 'JPY'
  const chartData = data.map((m) => ({
    label: m.label,
    amount: Math.abs(moneyValue(m.amount)),
    formatted: m.amount.formatted,
  }))

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-[15px] font-semibold">{category.name} by Month</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={chartData} barCategoryGap="24%">
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="oklch(0.91 0.004 80)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => formatChartValue(v, currency)}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null
                const monthData = chartData.find((m) => m.label === label)
                if (!monthData) return null
                return (
                  <div className="rounded-lg border border-border bg-card px-3 py-2 shadow-md">
                    <p className="text-[13px] font-medium text-foreground">{label}</p>
                    <p className="text-[13px] text-muted-foreground">{monthData.formatted}</p>
                  </div>
                )
              }}
            />
            <Bar dataKey="amount" name={category.name} fill={COSTS_COLOR} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}

function StatCard({
  title,
  money,
  percentage,
  percentageLabel,
  icon: Icon,
  trend,
}: {
  title: string
  money: Money
  percentage?: string
  percentageLabel?: string
  icon: React.ElementType
  trend: 'up' | 'down'
}) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-[13px] font-medium text-muted-foreground">{title}</p>
            <MoneyDisplay money={money} size="xl" />
          </div>
          <div
            className={cn(
              'rounded-lg p-2',
              trend === 'up' ? 'bg-success/8 text-success' : 'bg-destructive/8 text-destructive'
            )}
          >
            <Icon className="h-4 w-4" />
          </div>
        </div>
        {percentage && (
          <div className="mt-3 flex items-center gap-1.5">
            {trend === 'up' ? (
              <ArrowUpRightIcon className="h-3.5 w-3.5 text-success" />
            ) : (
              <ArrowDownRightIcon className="h-3.5 w-3.5 text-destructive" />
            )}
            <span
              className={cn(
                'text-[13px] font-medium',
                trend === 'up' ? 'text-success' : 'text-destructive'
              )}
            >
              {percentage}
            </span>
            <span className="text-[13px] text-muted-foreground">
              {percentageLabel ?? 'of revenue'}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function MonthlySummaryCard({
  monthData,
  prevYearName,
}: {
  monthData: MonthlyData | null | undefined
  prevYearName: string
}) {
  if (!monthData) return null

  const revenue = monthData.revenue
  const costs = monthData.costs
  const profit = monthData.net

  const prevRevenue = monthData.prev_revenue
  const prevCosts = monthData.prev_costs
  const prevProfit = { cents: prevRevenue.cents + prevCosts.cents }

  const monthRevenueYoy = yoyChange(revenue.cents, prevRevenue.cents)
  const monthCostsYoy = yoyChange(Math.abs(costs.cents), Math.abs(prevCosts.cents))
  const monthProfitYoy = yoyChange(profit.cents, prevProfit.cents)

  const monthCostsPercent = revenue.cents !== 0
    ? Math.min(Math.abs(costs.cents) / revenue.cents * 100, 100)
    : 0
  const monthProfitPercent = revenue.cents !== 0
    ? Math.max(profit.cents / revenue.cents * 100, 0)
    : 0

  return (
    <Card className="flex-1">
      <CardContent className="p-5">
        <p className="text-[13px] font-semibold text-foreground mb-4">{monthData.label} Summary</p>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <p className="text-[12px] font-medium text-muted-foreground">Revenue</p>
              {monthRevenueYoy && (
                <span className={cn(
                  'text-[10px] font-medium',
                  monthRevenueYoy.trend === 'up' ? 'text-success' : 'text-destructive'
                )}>
                  {monthRevenueYoy.percent}
                </span>
              )}
            </div>
            <MoneyDisplay money={revenue} size="lg" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <p className="text-[12px] font-medium text-muted-foreground">Costs</p>
              {monthCostsYoy && (
                <span className={cn(
                  'text-[10px] font-medium',
                  monthCostsYoy.trend === 'up' ? 'text-destructive' : 'text-success'
                )}>
                  {monthCostsYoy.percent}
                </span>
              )}
            </div>
            <MoneyDisplay money={costs} size="lg" />
          </div>
          <div className="space-y-1">
            <div className="flex items-center gap-1.5">
              <p className="text-[12px] font-medium text-muted-foreground">Profit</p>
              {monthProfitYoy && (
                <span className={cn(
                  'text-[10px] font-medium',
                  monthProfitYoy.trend === 'up' ? 'text-success' : 'text-destructive'
                )}>
                  {monthProfitYoy.percent}
                </span>
              )}
            </div>
            <MoneyDisplay money={profit} size="lg" />
          </div>
        </div>

        <div className="mt-4">
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted/50">
            <div
              className="h-full rounded-l-full bg-destructive/60 transition-all"
              style={{ width: `${monthCostsPercent}%` }}
            />
            <div
              className="h-full rounded-r-full bg-success/60 transition-all"
              style={{ width: `${monthProfitPercent}%` }}
            />
          </div>
          <div className="mt-1.5 flex items-center gap-3">
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-destructive/60" />
              <span className="text-[10px] text-muted-foreground">Costs {monthCostsPercent.toFixed(1)}%</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="h-1.5 w-1.5 rounded-full bg-success/60" />
              <span className="text-[10px] text-muted-foreground">Profit {monthProfitPercent.toFixed(1)}%</span>
            </div>
            {monthRevenueYoy && (
              <span className="ml-auto text-[10px] text-muted-foreground">
                vs {prevYearName}
              </span>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

function ReimbursementCard({
  title,
  money,
  linkHref,
  highlight = false,
}: {
  title: string
  money: Money
  linkHref?: string
  highlight?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-lg border p-4',
        highlight ? 'border-primary/20 bg-primary/5' : 'border-border'
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-[13px] font-medium text-muted-foreground">{title}</p>
        {linkHref && (
          <Link href={linkHref}>
            <ExternalLinkIcon className="h-3.5 w-3.5 text-primary hover:text-primary/80 transition-colors" />
          </Link>
        )}
      </div>
      <div className="mt-2">
        <MoneyDisplay money={money} size="lg" />
      </div>
    </div>
  )
}
